/**
 * MonthlyReportHtml — renders the exact HTML structure from ogiso_monthly_report_v2.html
 * This is captured by html2canvas → jsPDF for the monthly ADL report PDF.
 *
 * IMPORTANT: Every column gets an explicit pixel width via <colgroup> so that
 * html2canvas honours the layout at the full container width (1400px).
 */
import React, { useState, useEffect } from "react";
import moment from "moment";

/* Fetches an S3/remote URL and renders it as a base64 img to avoid CORS issues */
const SigImg = ({ url, style }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return;
    // If already a data URL, use directly
    if (url.startsWith("data:")) { setSrc(url); return; }
    fetch(url, { mode: "cors" })
      .then((r) => r.blob())
      .then((blob) => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      }))
      .then(setSrc)
      .catch(() => setSrc(url)); // fallback to direct URL
  }, [url]);
  if (!src) return null;
  return <img src={src} alt="" crossOrigin="anonymous" style={style} />;
};

/* ── container width used for PDF capture (set in forPdfBox CSS) ── */
const CONTAINER_W = 1400;
const PAGE_PAD = 56; // 28px padding each side
const INNER_W = CONTAINER_W - PAGE_PAD;
const GROUP_COL_W = 80;
const TASK_COL_W = 180;

const S = {
  page: {
    background: "#fff",
    padding: "28px 28px",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: 12,
    color: "#1a1a1a",
    width: CONTAINER_W,
    boxSizing: "border-box",
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingBottom: 12,
    borderBottom: "2px solid #1a1a1a",
    marginBottom: 14,
  },
  branding: { display: "flex", alignItems: "center", gap: 9 },
  logoCircle: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "#E6A817",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  facilityName: { fontSize: 16, fontWeight: 700, color: "#1a1a1a" },
  facilitySub: { fontSize: 11, color: "#777", marginTop: 1 },
  reportRight: { textAlign: "right" },
  reportTitle: { fontSize: 19, fontWeight: 700, color: "#1a1a1a" },
  reportSub: { fontSize: 12, color: "#777", marginTop: 2 },
  metaBlock: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 0,
    border: "0.5px solid #ccc",
    marginBottom: 14,
  },
  metaCell: { padding: "6px 10px", borderRight: "0.5px solid #ccc" },
  metaCellLast: { padding: "6px 10px" },
  metaCellBorder: {
    padding: "6px 10px",
    borderRight: "0.5px solid #ccc",
    borderTop: "0.5px solid #ccc",
  },
  metaCellBorderLast: { padding: "6px 10px", borderTop: "0.5px solid #ccc" },
  metaLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#888",
    marginBottom: 2,
  },
  metaValue: { fontSize: 13, fontWeight: 500, color: "#1a1a1a" },
  staffGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0,
    border: "0.5px solid #ccc",
    marginBottom: 14,
  },
  staffHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 60px 90px",
    background: "#f0eeea",
    borderBottom: "0.5px solid #ccc",
    padding: "4px 10px",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#777",
  },
  staffRow: {
    display: "grid",
    gridTemplateColumns: "1fr 60px 90px",
    padding: "5px 10px",
    alignItems: "center",
  },
  sigBox: { border: "0.5px solid #ddd", height: 24, background: "#fff" },
  matrixWrap: {
    overflowX: "auto",
    overflowY: "visible",
    border: "0.5px solid #ccc",
    width: "100%",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    marginTop: 14,
    borderTop: "0.5px solid #ccc",
    fontSize: 9,
    color: "#999",
  },
};

const MonthlyReportHtml = ({
  data = [],
  datesArray = [],
  selectedClient,
  monthValue,
  staffs = [],
  getInitials,
  facilityName,
  facilityPhoto,
  forPdf = false,
  pageOnly = false,
  allSignature = [],
  decryptFn,
  taskTypeName: taskTypeNameProp,
}) => {
  const facName =
    facilityName || selectedClient?.facility?.name || "Facility";
  const clientName = selectedClient?.name || "\u2014";
  const dob = selectedClient?.form1?.dob
    ? moment(selectedClient.form1.dob).format("MM/DD/YYYY")
    : "\u2014";
  const admissionDate =
    selectedClient?.form1?.admissionDate ||
    selectedClient?.form1?.admissiondate;
  const admDate = admissionDate
    ? moment(admissionDate).format("MM/DD/YYYY")
    : "\u2014";
  const allergies =
    selectedClient?.form1?.knownAllergies?.join(", ") || "None";
  const sex = selectedClient?.form1?.gender || "\u2014";
  const taskTypeName =
    taskTypeNameProp || (typeof data?.[0]?.taskType === "string" ? data[0].taskType : data?.[0]?.taskType?.name) || "Activities of Daily Living";
  const reportMonth = monthValue
    ?.map((m) => (moment(m).isValid() ? moment(m).format("MMM YYYY") : ""))
    .join(" \u2013 ");
  const todayNum = new Date().getDate();
  const todayMonth = new Date().getMonth();

  // Build days array from datesArray
  const days = datesArray.map((d) => ({
    num: d.date(),
    name: d.format("ddd"),
    dow: d.day(),
    monthIdx: d.month(),
    dateStr: d.format("YYYY-MM-DD"),
  }));

  // Calculate explicit day-column width
  const dayColW = days.length > 0
    ? Math.floor((INNER_W - GROUP_COL_W - TASK_COL_W) / days.length)
    : 30;

  // Unique staff
  const uniqueStaff = staffs?.filter((v) => v && v.id) || [];

  return (
    <div style={{ ...S.page, width: forPdf ? CONTAINER_W : "100%" }} data-facility-name={facName}>

      {/* pageOnly mode: just the month label, no full header */}
      {pageOnly ? (
        <div style={{
          paddingBottom: 10,
          marginBottom: 12,
          borderBottom: "2px solid #1a1a1a",
          display: "flex",
          alignItems: "baseline",
          gap: 10,
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>
            {datesArray.length > 0
              ? moment(typeof datesArray[0].format === "function" ? datesArray[0].toDate() : datesArray[0]).format("MMMM YYYY")
              : monthValue?.[0]
              ? moment(monthValue[0]).format("MMMM YYYY")
              : reportMonth}
          </span>
          {/* <span style={{ fontSize: 12, color: "#888" }}>
            {facName} &nbsp;&middot;&nbsp; {taskTypeName}
          </span> */}
        </div>
      ) : (
        <>
          {/* HEADER */}
          <div style={S.pageHeader}>
            <div style={S.branding}>
              {facilityPhoto ? (
                <img
                  src={facilityPhoto}
                  alt=""
                  crossOrigin="anonymous"
                  style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                />
              ) : (
                <div style={S.logoCircle}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{facName?.[0] || "O"}</span>
                </div>
              )}
              <div>
                <div style={S.facilityName}>{facName}</div>
                <div style={S.facilitySub}>
                  Monthly {taskTypeName} Report
                </div>
              </div>
            </div>
            <div style={S.reportRight}>
              <div style={S.reportSub}>
                Generated {moment().format("MM/DD/YYYY")}
              </div>
            </div>
          </div>

          {/* META BLOCK */}
          <div style={S.metaBlock}>
            <div style={S.metaCell}>
              <div style={S.metaLabel}>Resident name</div>
              <div style={S.metaValue}>{clientName}</div>
            </div>
            <div style={S.metaCell}>
              <div style={S.metaLabel}>Date of birth</div>
              <div style={S.metaValue}>{dob}</div>
            </div>
            <div style={S.metaCell}>
              <div style={S.metaLabel}>Admission date</div>
              <div style={S.metaValue}>{admDate}</div>
            </div>
            <div style={S.metaCellLast}>
              <div style={S.metaLabel}>Allergies</div>
              <div style={S.metaValue}>{allergies}</div>
            </div>
            <div style={S.metaCellBorder}>
              <div style={S.metaLabel}>Sex</div>
              <div style={S.metaValue}>{sex}</div>
            </div>
            <div style={S.metaCellBorder}>
              <div style={S.metaLabel}>Task type</div>
              <div style={S.metaValue}>{taskTypeName}</div>
            </div>
            <div style={S.metaCellBorder}>
              <div style={S.metaLabel}>Report month</div>
              <div style={S.metaValue}>{reportMonth}</div>
            </div>
            <div style={S.metaCellBorderLast}>
              <div style={S.metaLabel}>Report date</div>
              <div style={S.metaValue}>{moment().format("MM/DD/YYYY")}</div>
            </div>
          </div>

          {/* STAFF TABLE */}
          <div style={S.staffGrid}>
            {uniqueStaff.map(({ name, id }, idx) => {
              const staffSig = allSignature?.find(
                (s) => (s?.staff && s.staff?.id === id) || (s?.admin && s.admin?.id === id)
              );
              const initialsContent = getInitials ? getInitials(name, id) : (
                <span style={{ fontSize: 11 }}>{name?.split(" ").map(w => w?.[0]?.toUpperCase()).join("")}</span>
              );
              let sigContent = null;
              try {
                const rawSig = staffSig?.signature || staffSig?.signatureImage;
                const decryptedSig = rawSig && decryptFn ? decryptFn(rawSig) : null;
                if (decryptedSig) {
                  sigContent = <SigImg url={decryptedSig} style={{ height: 32, display: "inline-block", objectFit: "contain" }} />;
                } else if (staffSig?.signatureText) {
                  sigContent = <span style={{ fontSize: 22, fontFamily: "'Island Moments', cursive", color: "#171717" }}>{staffSig.signatureText}</span>;
                }
              } catch (e) {}
              return (
                <div key={idx} style={idx < uniqueStaff.length - 1 ? { borderRight: "0.5px solid #ccc" } : {}}>
                  <div style={S.staffHeader}>
                    <span>Staff name</span>
                    <span>Initial</span>
                    <span>Signature</span>
                  </div>
                  <div style={S.staffRow}>
                    <span style={{ fontSize: 11 }}>{name || "N/A"}</span>
                    <span style={{ fontSize: 11 }}>{initialsContent || "N/A"}</span>
                    <div style={{ ...S.sigBox, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {sigContent || "N/A"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* MATRIX */}
      <div style={S.matrixWrap}>
        <table
          style={{
            borderCollapse: "collapse",
            width: forPdf ? INNER_W : "100%",
            minWidth: forPdf ? undefined : GROUP_COL_W + TASK_COL_W + days.length * 36,
            tableLayout: "fixed",
          }}
        >
          {/* Explicit colgroup so html2canvas respects column widths */}
          <colgroup>
            <col style={{ width: forPdf ? GROUP_COL_W : 70 }} />
            <col style={{ width: forPdf ? TASK_COL_W : 140 }} />
            {days.map((_, i) => (
              <col key={i} style={{ width: forPdf ? dayColW : undefined }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                style={{
                  background: "#1e2533",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  padding: "5px 4px",
                  textAlign: "left",
                  border: "0.5px solid #ddd",
                }}
              />
              <th
                style={{
                  background: "#f0eeea",
                  color: "#666",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  padding: "4px 6px",
                  textAlign: "left",
                  border: "0.5px solid #ddd",
                }}
              >
                Task
              </th>
              {days.map(({ num, name, dow }, i) => {
                const isWE = dow === 0 || dow === 6;
                const isToday = num === todayNum && days[i].monthIdx === todayMonth;
                let bg = "#f0eeea";
                let color = undefined;
                if (isWE) bg = "#e8e4da";
                if (isToday) {
                  bg = "#E6A817";
                  color = "#fff";
                }
                return (
                  <th
                    key={i}
                    style={{
                      background: bg,
                      textAlign: "center",
                      padding: "2px 0",
                      border: "0.5px solid #ddd",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        color: color || "#999",
                        fontWeight: 400,
                        display: "block",
                      }}
                    >
                      {name}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: color || "#1a1a1a",
                        fontWeight: 600,
                        display: "block",
                      }}
                    >
                      {num}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((group, gi) => {
              const taskCount = group.fields?.length || 1;
              return group.fields?.map((field, fi) => {
                const globalRow =
                  data
                    .slice(0, gi)
                    .reduce((acc, g) => acc + (g.fields?.length || 0), 0) + fi;
                const isAlt = globalRow % 2 === 1;
                return (
                  <tr key={`${gi}-${fi}`}>
                    {fi === 0 && (
                      <td
                        rowSpan={taskCount}
                        style={{
                          background: "#1e2533",
                          verticalAlign: "middle",
                          textAlign: "center",
                          border: "0.5px solid #ddd",
                          padding: forPdf ? "6px 2px" : 0,
                        }}
                      >
                        <span
                          style={forPdf ? {
                            display: "block",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#fff",
                            textTransform: "uppercase",
                            letterSpacing: 0.3,
                            lineHeight: 1.2,
                            wordBreak: "break-word",
                            textAlign: "center",
                          } : {
                            display: "block",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#fff",
                            textTransform: "uppercase",
                            letterSpacing: 0.3,
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            whiteSpace: "nowrap",
                            padding: "4px 0",
                          }}
                        >
                          {group.taskName || "\u2014"}
                        </span>
                      </td>
                    )}
                    <td
                      style={{
                        fontSize: 14,
                        color: "#333",
                        padding: "4px 6px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        background: isAlt ? "#fafaf8" : "#fff",
                        border: "0.5px solid #ddd",
                      }}
                    >
                      {field.name || "\u2014"}
                    </td>
                    {days.map(({ num, dow, dateStr, monthIdx: mIdx }, di) => {
                      const isWE = dow === 0 || dow === 6;
                      const isToday = num === todayNum && mIdx === todayMonth;
                      let bg = isAlt ? "#fafaf8" : "#fff";
                      if (isWE) bg = isAlt ? "#f4f2ec" : "#f9f8f4";
                      if (isToday) bg = isAlt ? "#fff8e6" : "#fffbf0";

                      // Find data for this field on this date
                      const found = field.date_array?.find(
                        (x) => moment(x.date).format("YYYY-MM-DD") === dateStr
                      );
                      let cellContent = null;
                      if (found?.isChecked?.length > 0) {
                        cellContent = found.isChecked.map((fc, ci) => {
                          const init =
                            getInitials(fc?.staff?.name, fc?.staff?.id) || "";
                          const val = fc?.textField || "";
                          const initText =
                            typeof init === "string"
                              ? init
                              : fc?.staff?.name
                                  ?.split(" ")
                                  .map((w) => w[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2) || "";
                          return (
                            <div key={ci}>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#1a1a1a",
                                  display: "block",
                                  lineHeight: 1,
                                }}
                              >
                                {initText}
                              </span>
                              {val && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "#444",
                                    display: "block",
                                    lineHeight: 1.2,
                                    marginTop: 1,
                                  }}
                                >
                                  {val}
                                </span>
                              )}
                            </div>
                          );
                        });
                      }
                      return (
                        <td
                          key={di}
                          style={{
                            textAlign: "center",
                            padding: "1px 1px",
                            background: bg,
                            height: 22,
                            verticalAlign: "middle",
                            border: "0.5px solid #ddd",
                          }}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      {/* FOOTER — hidden when captured for PDF (jsPDF draws it instead) */}
      {!forPdf && (
        <div style={S.footer}>
          <span>
            {facName} &middot; Confidential &mdash; Report generated by Ogiso
          </span>
        </div>
      )}
    </div>
  );
};

export default MonthlyReportHtml;
